import type { IPlatformHandlers } from '@wix/editor-elements-types/corvid';
import { assert } from '../../../common/sdk/assert';
import { composeSDKFactories } from '../../../common/sdk/composeSDKFactories';
import { createComponentSDKModel } from '../../../common/sdk/createCorvidModel';
import { messages } from '../../../common/sdk/messages';
import { reportError, reportWarning } from '../../../common/sdk/reporters';
import {
  createSvgWixMediaUrl,
  resolveAndFetchSvg,
} from '../../../common/sdk/svg';
import { withValidation } from '../../../common/sdk/validations/withValidation';
import { clickPropsSDKFactory } from '../../../common/sdk/props-factories/clickPropsSDKFactory';
import { createAccessibilityPropSDKFactory } from '../../../common/sdk/props-factories/accessibilityPropsSDKFactory';
import {
  createElementPropsSDKFactory,
  toJSONBase,
} from '../../../common/sdk/props-factories/elementPropsSDKFactory';
import { createStylePropsSDKFactory } from '../../../common/sdk/props-factories/stylePropsSDKFactory';
import { disablePropsSDKFactory } from '../../../common/sdk/props-factories/disablePropsSDKFactory';
import { focusPropsSDKFactory } from '../../../common/sdk/props-factories/focusPropsSDKFactory';
import { labelPropsSDKFactory } from '../../../common/sdk/props-factories/labelPropsSDKFactory';
import { linkPropsSDKFactory } from '../../../common/sdk/props-factories/linkPropsSDKFactory';
import type { StyleState } from '../../../common/sdk/props-factories/styleSDKFactories/types';
import {
  IconAnimationTriggers,
  type IStylableButtonOwnSDKFactory,
  type IStylableButtonSDK,
  type StyleProps,
  type StylableButtonSdkData,
} from './Button.corvid.types';

export const BUTTON_SDK_TYPE = '$w.Button';

export const supportedStyleStates: Array<StyleState> = ['hover', 'disabled'];
export const stylePropsSDKFactory = createStylePropsSDKFactory({
  BackgroundColor: {
    withoutDefaultValue: true,
    supportOpacity: true,
    supportedStates: supportedStyleStates,
  },
  BorderColor: {
    withoutDefaultValue: true,
    supportOpacity: false,
    supportedStates: supportedStyleStates,
  },
  BorderRadius: { withoutDefaultValue: true },
  BorderWidth: { withoutDefaultValue: true },
  TextColor: {
    withoutDefaultValue: true,
    supportedStates: supportedStyleStates,
  },
  IconColor: {
    withoutDefaultValue: true,
    supportedStates: supportedStyleStates,
  },
});

const fetchIconSvgString = async (
  value: string,
  mediaSvgUrl: string,
  corvidProps: any,
  sanitizeSVG: IPlatformHandlers['sanitizeSVG'],
) => {
  const svg = await resolveAndFetchSvg(value, mediaSvgUrl, sanitizeSVG);
  return { corvid: { ...corvidProps, iconSvgString: svg } };
};

const _stylableButtonSDKFactory: IStylableButtonOwnSDKFactory = api => {
  const labelSDK = labelPropsSDKFactory(api);
  const styleSDK = stylePropsSDKFactory(api);
  const { props, setProps, sdkData, createSdkState, handlers, compRef } = api;
  const [state, setState] = createSdkState<{
    iconMediaUrl?: string | null;
  }>({});

  const mapPropertyNameToCorvidPropertyName: Partial<
    Record<keyof StyleProps, string>
  > = {
    backgroundColor: 'hasBackgroundColor',
    borderWidth: 'hasBorderWidth',
    borderRadius: 'hasBorderRadius',
    borderColor: 'hasBorderColor',
    iconColor: 'hasIconColor',
    color: 'hasColor',
  };

  const contributeStyleSDK =
    (propertyName: keyof StyleProps, styleState?: StyleState) =>
    (target: any) => {
      if (styleState && !target[styleState]) {
        target[styleState] = {};
      }

      const sdk: any = styleState ? styleSDK.style[styleState] : styleSDK.style;
      const targetSdk = styleState ? target[styleState] : target;

      Object.defineProperty(targetSdk, propertyName, {
        enumerable: true,
        get() {
          return sdk[propertyName];
        },
        set(value: string | undefined) {
          sdk[propertyName] = value;

          const statePropertyName =
            mapPropertyNameToCorvidPropertyName[propertyName]!;

          api.setProps({
            corvid: {
              ...api.props.corvid,
              ...(styleState
                ? {
                    [styleState]: {
                      ...api.props.corvid?.[styleState],
                      [statePropertyName]: true,
                    },
                  }
                : {
                    [statePropertyName]: true,
                  }),
            },
          });
        },
      });

      return target;
    };

  return {
    get label() {
      return labelSDK.label;
    },
    set label(value) {
      labelSDK.label = value;
    },
    get style() {
      return [
        contributeStyleSDK('backgroundColor'),
        contributeStyleSDK('borderColor'),
        contributeStyleSDK('borderRadius'),
        contributeStyleSDK('borderWidth'),
        contributeStyleSDK('iconColor'),
        contributeStyleSDK('color'),
        ...supportedStyleStates.flatMap(styleState => [
          contributeStyleSDK('backgroundColor', styleState),
          contributeStyleSDK('borderColor', styleState),
          contributeStyleSDK('iconColor', styleState),
          contributeStyleSDK('color', styleState),
        ]),
      ].reduce<object>((sdk, contribute) => contribute(sdk), {
        removeProperty(propertyName: string) {
          const [stylePropertyName, styleState] = propertyName
            .split('.')
            .reverse();

          const statePropertyName =
            mapPropertyNameToCorvidPropertyName[
              stylePropertyName as keyof StyleProps
            ];

          if (!statePropertyName) {
            return;
          }

          const propsToCorvid = {
            ...api.props.corvid,
            ...(styleState
              ? {
                  [styleState]: {
                    ...api.props.corvid?.[styleState as StyleState],
                    [statePropertyName]: false,
                  },
                }
              : {
                  [statePropertyName]: false,
                }),
          };

          styleSDK.style.removeProperty(propertyName as keyof StyleProps);
          api.setProps({ corvid: propsToCorvid });
        },
      });
    },
    get icon() {
      return (
        state.iconMediaUrl ||
        createSvgWixMediaUrl((sdkData as StylableButtonSdkData).svgId, '')
      );
    },
    set icon(value: string | null | undefined) {
      setState({ iconMediaUrl: value });
      if (value) {
        setProps(
          fetchIconSvgString(
            value,
            (sdkData as StylableButtonSdkData).mediaSvgUrl,
            props.corvid,
            handlers.sanitizeSVG,
          ),
        );
      } else {
        setProps({ corvid: { ...props.corvid, iconSvgString: null } });
      }
    },
    get iconCollapsed() {
      return !!props.corvid?.iconCollapsed;
    },
    set iconCollapsed(value: boolean) {
      setProps({ corvid: { ...props.corvid, iconCollapsed: value } });
    },
    collapseIcon() {
      setProps({ corvid: { ...props.corvid, iconCollapsed: true } });
    },
    expandIcon() {
      setProps({ corvid: { ...props.corvid, iconCollapsed: false } });
    },
    get iconAnimationTriggers() {
      return props.corvid?.iconAnimationTriggers || [];
    },
    set iconAnimationTriggers(value: Array<IconAnimationTriggers> | []) {
      if (value.length > 1) {
        value.length = 1;
      }
      setProps({ corvid: { ...props.corvid, iconAnimationTriggers: value } });
    },
    animateIconForward() {
      compRef.animateIconForward?.();
    },
    animateIconBackward() {
      compRef.animateIconBackward?.();
    },
    get type() {
      return BUTTON_SDK_TYPE;
    },
    toJSON() {
      const { label } = labelSDK;
      const { style } = styleSDK;

      return {
        ...toJSONBase(api.metaData),
        label,
        style: { ...style },
        type: BUTTON_SDK_TYPE,
      };
    },
  };
};

const elementPropsSDKFactory = createElementPropsSDKFactory();

export const accessibilityPropsSDKFactory = createAccessibilityPropSDKFactory({
  enableAriaLabel: true,
  enableAriaLabelledBy: true,
  enableAriaDescribedBy: true,
  enableAriaControls: true,
  enableAriaExpanded: true,
  enableAriaPressed: true,
  enableAriaOwns: true,
  enableAriaLive: true,
  enableAriaAtomic: true,
  enableAriaRelevant: true,
  enableAriaHaspopup: true,
  enableTabIndex: true,
  enableScreenReader: true,
  enableLang: true,
});

export const stylableButtonSDKFactory = withValidation(
  _stylableButtonSDKFactory,
  {
    type: ['object'],
    properties: {
      icon: { type: ['string', 'nil'] },
      iconAnimationTriggers: {
        type: ['array'],
        items: {
          type: ['string'],
          enum: [IconAnimationTriggers.HOVER as string],
        },
        name: 'iconAnimationTrigger',
      },
    },
  },
  {
    icon: [
      (value: string | null | undefined) => {
        if (value) {
          const isValid = assert.isSVG(value);
          if (!isValid) {
            reportError(messages.invalidSvgValue(value));
          }
        }
        return true;
      },
    ],
    animateIconForward: [
      (_: never, api: Parameters<IStylableButtonOwnSDKFactory>[0]) => {
        if (!api.props.isIconAnimated) {
          reportWarning(
            messages.animatedIconWarning({
              propertyName: 'animateIconForward',
            }),
          );
        }
        return true;
      },
    ],
    animateIconBackward: [
      (_: never, api: Parameters<IStylableButtonOwnSDKFactory>[0]) => {
        if (!api.props.isIconAnimated) {
          reportWarning(
            messages.animatedIconWarning({
              propertyName: 'animateIconBackward',
            }),
          );
        }
        return true;
      },
    ],
  },
);

export const ButtonSdk = composeSDKFactories<any, IStylableButtonSDK>([
  elementPropsSDKFactory,
  clickPropsSDKFactory,
  focusPropsSDKFactory,
  disablePropsSDKFactory,
  linkPropsSDKFactory,
  accessibilityPropsSDKFactory,
  stylableButtonSDKFactory,
]);

export default createComponentSDKModel(ButtonSdk).factory;
